/**
 * Loading state component with Dashboard3 glassmorphism design
 *
 * Modern loading animation with pulse effects and glassmorphism styling
 *
 * @example
 * ```tsx
 * <LoadingState message="Loading data..." />
 * ```
 */

"use client";

import { Box, Flex, keyframes } from "@chakra-ui/react";
import { useThemeTokens } from "@/lib/theme/tokens";
import { Text, Card } from "../ui";

export interface LoadingStateProps {
  /**
   * Minimum height
   */
  minH?: string;
  /**
   * Loading message
   */
  message?: string;
}

// Pulse animation for the loading dots
const pulse = keyframes`
  0%, 100% {
    opacity: 0.4;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`;

// Breathing animation for the outer glow
const breathe = keyframes`
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.1);
  }
`;

export function LoadingState({
  minH = "60vh",
  message = "Loading...",
}: LoadingStateProps) {
  const theme = useThemeTokens();

  return (
    <Flex
      gap={6}
      minH={minH}
      align="center"
      justify="center"
      direction="column"
    >
      <Card
        p={8}
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg={theme.accent.mutedBg}
        borderColor={theme.accent.primaryBorder}
        borderWidth="1.5px"
      >
        {/* Outer glow effect */}
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          w="80px"
          h="80px"
          borderRadius="full"
          bg={theme.accent.primary}
          opacity={0.2}
          filter="blur(12px)"
          animation={`${breathe} 2s ease-in-out infinite`}
        />

        {/* Loading dots */}
        <Flex gap={2} align="center" position="relative" zIndex={1}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              w="12px"
              h="12px"
              borderRadius="full"
              bg={theme.accent.primary}
              animation={`${pulse} 1.4s ease-in-out infinite`}
              style={{
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </Flex>
      </Card>

      {message && (
        <Text variant="secondary" fontSize="md" fontWeight="500">
          {message}
        </Text>
      )}
    </Flex>
  );
}
